var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { api } from "../api/client";
// The box is CODE-ONLY (Tim, 2026-09-17): it resolves a code the student has
// already found through the Alphabetic Index, Tabular List and ACS, and never
// searches by condition or procedure name. The server enforces the same gate
// (CodingKbClient.autocomplete); these shapes exist so the student gets the
// explanation instantly, with no request, when they type a term.
// ICD-10-AM = letter + 2 digits + optional .decimals (G20, E11.71, M79.604);
// morphology (Appendix A) = M + 4 digits / behaviour digit (M8140/3), assigned in
// the diagnoses section; ACHI = 4 to 5 digits + optional -NN extension (16012-00).
const ICD_CODE = /^[A-Za-z]\d{2}(?:\.\d{1,4})?$/;
const MORPH_CODE = /^[A-Za-z]\d{4}\/\d$/;
const ACHI_CODE = /^\d{4,5}(?:-\d{2})?$/;
// Still on the way to one of the shapes above (E, E1, E11., 1601, 16012-): stay
// quiet rather than telling a student mid-keystroke that "E1" is not a code.
const CODE_PREFIX = /^(?:[A-Za-z]\d{0,4}|[A-Za-z]\d{2}\.\d{0,4}|[A-Za-z]\d{4}\/|\d{1,5}(?:-\d{0,2})?)$/;
/** Classify what the student has typed, relative to the box's classification. */
export function classifyQuery(raw, classification) {
    const q = raw.trim();
    const icd = ICD_CODE.test(q) || MORPH_CODE.test(q);
    const achi = ACHI_CODE.test(q);
    if (icd || achi) {
        if (classification === "icd10am" && !icd)
            return "wrong-class";
        if (classification === "achi" && !achi)
            return "wrong-class";
        return "code";
    }
    return CODE_PREFIX.test(q) ? "prefix" : "term";
}
let instanceCounter = 0;
let CodeInput = class CodeInput extends LitElement {
    constructor() {
        super(...arguments);
        this.edition = "13e";
        this.classification = "";
        this.placeholder = "Type a code (e.g. E11.71)";
        this.fetchSuggestions = (q, e, c) => api.autocomplete(q, e, c);
        this.instanceId = `code-input-${++instanceCounter}`;
        this.q = "";
        this.status = "idle";
        this.suggestions = [];
        this.highlight = -1;
        this.dropdownPos = null;
        this.timer = null;
        this.requestSeq = 0;
        this.listening = false;
        this.updatePosition = () => {
            const rect = this.inputEl?.getBoundingClientRect();
            if (!rect)
                return;
            this.dropdownPos = { top: rect.bottom + 4, left: rect.left, width: rect.width };
        };
    }
    static { this.styles = css `
    :host, *, *::before, *::after { box-sizing: border-box; }
    :host { display: block; position: relative; }
    input { display: block; width: 100%; min-width: 0; padding: 9px 12px; border: 1px solid var(--color-slate-300);
            border-radius: var(--radius-md); font-family: var(--font-mono); font-size: 13px;
            color: var(--color-slate-800); background: var(--color-white); }
    input:focus { outline: 2px solid var(--color-blue-500); outline-offset: -1px;
                  border-color: var(--color-blue-500); }
    input::placeholder { font-family: var(--font-base); color: var(--color-slate-400); }
    /* position: fixed (anchored to the input's own getBoundingClientRect, see
       updatePosition()) rather than position: absolute inside the host. This
       component is used inside ancestors with overflow:hidden/auto (the
       abstract-mode .section card and the case-view scroll pane) — an
       absolutely-positioned dropdown gets clipped by those, but a fixed one
       escapes any ancestor's overflow/clip box since none of them establish a
       new containing block (no transform/filter/contain/perspective). Kept in
       sync with the input's position on scroll/resize while open. */
    ul { position: fixed; margin: 0; padding: 4px 0; list-style: none; background: var(--color-white);
         border: 1px solid var(--color-slate-300); border-radius: var(--radius-md);
         box-shadow: var(--shadow-md); z-index: 5000; max-height: 300px; overflow-y: auto;
         min-width: 220px; }
    li { padding: 8px 12px; cursor: pointer; font-size: 13px; display: flex; gap: 10px; align-items: baseline; }
    li:hover, li[aria-selected="true"] { background: var(--color-blue-100); }
    li.status-row { cursor: default; color: var(--color-slate-500); font-style: italic; line-height: 1.4; }
    li.status-row.error-row { color: #B91C1C; }
    code { font-family: var(--font-mono); font-weight: 600; color: var(--color-blue-600);
           min-width: 70px; flex: 0 0 auto; }
    .desc { color: var(--color-slate-700); }
  `; }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.stopListening();
        if (this.timer)
            clearTimeout(this.timer);
    }
    onInput(e) {
        this.q = e.target.value;
        if (this.timer)
            clearTimeout(this.timer);
        const shape = this.q.trim().length < 2 ? "prefix" : classifyQuery(this.q, this.classification);
        if (shape !== "code") {
            // Nothing is sent for a term or a wrong-classification code: the guidance
            // row IS the response. A prefix just closes the list until the code is whole.
            this.requestSeq++; // drop any in-flight lookup for an earlier value
            this.suggestions = [];
            this.highlight = -1;
            if (shape === "prefix") {
                this.closeDropdown();
                return;
            }
            this.status = shape === "term" ? "not-code" : "wrong-class";
            this.openDropdown();
            return;
        }
        const q = this.q.trim();
        const edition = this.edition;
        const cls = this.classification || undefined;
        this.timer = setTimeout(() => this.runSearch(q, edition, cls), 150);
    }
    runSearch(q, edition, cls) {
        const seq = ++this.requestSeq;
        this.highlight = -1;
        this.status = "loading";
        this.openDropdown();
        this.fetchSuggestions(q, edition, cls).then((results) => {
            if (seq !== this.requestSeq)
                return; // stale response, a newer search superseded it
            this.suggestions = results;
            this.status = results.length ? "results" : "empty";
        }, () => {
            if (seq !== this.requestSeq)
                return;
            this.suggestions = [];
            this.status = "error";
        });
    }
    onKeydown(e) {
        const items = this.suggestions;
        const open = this.status !== "idle";
        if (e.key === "ArrowDown") {
            if (!open || items.length === 0)
                return;
            e.preventDefault();
            this.highlight = (this.highlight + 1) % items.length;
        }
        else if (e.key === "ArrowUp") {
            if (!open || items.length === 0)
                return;
            e.preventDefault();
            this.highlight = this.highlight <= 0 ? items.length - 1 : this.highlight - 1;
        }
        else if (e.key === "Enter") {
            if (this.highlight >= 0 && items[this.highlight]) {
                e.preventDefault();
                this.pick(items[this.highlight]);
            }
        }
        else if (e.key === "Escape") {
            if (open) {
                e.preventDefault();
                if (this.timer) {
                    clearTimeout(this.timer);
                    this.timer = null;
                }
                this.suggestions = [];
                this.highlight = -1;
                this.closeDropdown();
            }
        }
    }
    pick(s) {
        this.dispatchEvent(new CustomEvent("code-pick", {
            detail: { code: s.code, description: s.description, classification: this.classification || undefined },
            bubbles: true, composed: true,
        }));
        this.q = "";
        this.suggestions = [];
        this.highlight = -1;
        this.closeDropdown();
    }
    openDropdown() {
        this.updatePosition();
        if (this.listening)
            return;
        this.listening = true;
        window.addEventListener("scroll", this.updatePosition, { capture: true, passive: true });
        window.addEventListener("resize", this.updatePosition, { passive: true });
    }
    closeDropdown() {
        this.status = "idle";
        this.dropdownPos = null;
        this.stopListening();
    }
    stopListening() {
        if (!this.listening)
            return;
        this.listening = false;
        window.removeEventListener("scroll", this.updatePosition, true);
        window.removeEventListener("resize", this.updatePosition);
    }
    classificationLabel() {
        return this.classification === "icd10am" ? "ICD-10-AM" : this.classification === "achi" ? "ACHI" : "ICD-10-AM or ACHI";
    }
    exampleCode() {
        return this.classification === "icd10am" ? "E11.71" : this.classification === "achi" ? "30473-00" : "E11.71 or 30473-00";
    }
    wrongClassMessage() {
        return this.classification === "achi"
            ? "That looks like an ICD-10-AM diagnosis code. This box takes ACHI procedure codes (for example 30473-00)."
            : "That looks like an ACHI procedure code. This box takes ICD-10-AM diagnosis codes (for example E11.71).";
    }
    render() {
        const open = this.status !== "idle";
        const activeId = this.highlight >= 0 && this.suggestions[this.highlight]
            ? `${this.instanceId}-opt-${this.highlight}` : undefined;
        const listboxId = `${this.instanceId}-listbox`;
        const dropdown = open && this.dropdownPos ? html `
      <ul role="listbox" id=${listboxId}
          style="top:${this.dropdownPos.top}px; left:${this.dropdownPos.left}px; width:${this.dropdownPos.width}px;">
        ${this.status === "loading" ? html `<li class="status-row">Looking up code...</li>` : ""}
        ${this.status === "empty" ? html `
          <li class="status-row">No ${this.classificationLabel()} code matches "${this.q.trim()}". Check the code in the Tabular List and type it exactly (for example ${this.exampleCode()}).</li>` : ""}
        ${this.status === "not-code" ? html `
          <li class="status-row">This box looks up codes only, not conditions or procedures. Find the code in the Alphabetic Index and Tabular List, then type it here (for example ${this.exampleCode()}).</li>` : ""}
        ${this.status === "wrong-class" ? html `
          <li class="status-row">${this.wrongClassMessage()}</li>` : ""}
        ${this.status === "error" ? html `
          <li class="status-row error-row" role="alert">Code lookup is unavailable right now. Wait a moment and type the code again.</li>` : ""}
        ${this.status === "results" ? this.suggestions.map((s, i) => html `
          <li id="${this.instanceId}-opt-${i}" role="option" aria-selected=${i === this.highlight}
              data-code=${s.code} @click=${() => this.pick(s)} @mouseenter=${() => { this.highlight = i; }}>
            <code>${s.code}</code><span class="desc">${s.description}</span>
          </li>`) : ""}
      </ul>` : "";
        return html `
      <input type="text" .value=${this.q} @input=${this.onInput} @keydown=${this.onKeydown}
             placeholder=${this.placeholder}
             aria-label="ICD/ACHI code" autocomplete="off"
             role="combobox" aria-autocomplete="list" aria-expanded=${open ? "true" : "false"}
             aria-controls=${listboxId}
             aria-activedescendant=${ifDefined(activeId)} />
      ${dropdown}
    `;
    }
};
__decorate([
    property({ type: String })
], CodeInput.prototype, "edition", void 0);
__decorate([
    property({ type: String })
], CodeInput.prototype, "classification", void 0);
__decorate([
    property({ type: String })
], CodeInput.prototype, "placeholder", void 0);
__decorate([
    property({ attribute: false })
], CodeInput.prototype, "fetchSuggestions", void 0);
__decorate([
    state()
], CodeInput.prototype, "q", void 0);
__decorate([
    state()
], CodeInput.prototype, "status", void 0);
__decorate([
    state()
], CodeInput.prototype, "suggestions", void 0);
__decorate([
    state()
], CodeInput.prototype, "highlight", void 0);
__decorate([
    state()
], CodeInput.prototype, "dropdownPos", void 0);
__decorate([
    query("input")
], CodeInput.prototype, "inputEl", void 0);
CodeInput = __decorate([
    customElement("code-input")
], CodeInput);
export { CodeInput };
